import { forwardRef } from 'react';
import { Button } from 'react-bootstrap';
import type { ButtonProps } from 'react-bootstrap';
import type { ReactNode } from 'react';
import { InlineLoader } from '../feedback/LoadingSpinner';

export interface AppButtonProps extends Omit<ButtonProps, 'variant'> {
  /**
   * Visual variant. Maps 1-to-1 to Bootstrap/Tabler button variants.
   * 'ghost' is a custom variant (transparent background, subtle hover).
   */
  variant?:
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'success'
    | 'warning'
    | 'info'
    | 'outline-primary'
    | 'outline-secondary'
    | 'outline-danger'
    | 'outline-info'
    | 'outline-success'
    | 'link'
    | 'ghost';
  /** Show inline spinner and disable the button while true */
  loading?: boolean;
  /** Text shown instead of children when loading=true */
  loadingText?: ReactNode;
  /** Icon rendered before children (or replaces spinner slot when loading) */
  icon?: ReactNode;
  /** Position of the icon. Default: 'start' */
  iconPosition?: 'start' | 'end';
}

/**
 * AppButton — wrapper around react-bootstrap Button with:
 * - loading state (spinner replaces icon)
 * - icon slot with position control
 * - 'ghost' custom variant
 *
 * @example
 * <AppButton variant="primary" icon={<IconPlus size={16} />} loading={isSubmitting}>
 *   Save
 * </AppButton>
 */
const AppButton = forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      variant = 'secondary',
      loading = false,
      loadingText,
      icon,
      iconPosition = 'start',
      disabled,
      children,
      className = '',
      ...rest
    },
    ref
  ) => {
    // Map 'ghost' to a real Bootstrap variant + extra class
    const isGhost = variant === 'ghost';
    const bsVariant = isGhost ? 'link' : variant;
    const extraClass = isGhost ? 'btn-ghost' : '';

    const iconNode = loading ? <InlineLoader size="sm" className={children || loadingText ? (iconPosition === 'end' ? 'ms-1' : 'me-1') : ''} /> : icon;
    const label    = loading ? (loadingText ?? children) : children;

    return (
      <Button
        ref={ref}
        variant={bsVariant}
        disabled={disabled || loading}
        className={[extraClass, className].filter(Boolean).join(' ')}
        {...rest}
      >
        {iconPosition === 'start' && iconNode && <span className={`btn-icon-start${label ? ' me-1' : ''}`}>{iconNode}</span>}
        {label}
        {iconPosition === 'end'   && iconNode && <span className={`btn-icon-end${label ? ' ms-1' : ''}`}>{iconNode}</span>}
      </Button>
    );
  }
);

AppButton.displayName = 'AppButton';
export default AppButton;
